// Row editing pieces shared by the family page and the decisions page: the "…" menu and
// the one-field inline form.

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

export function RowMenu({
  open,
  onToggle,
  items,
  onPick,
  name,
  rowId,
}: {
  open: boolean;
  onToggle: () => void;
  items: { label: string; run: () => void }[];
  onPick: () => void;
  name: string;
  /** Marks the "…" button so focus can return to it after a confirmation closes. */
  rowId?: string;
}): JSX.Element {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef(onPick);
  closeRef.current = onPick;

  // While open: close on a press outside the menu and its button, and on Escape (focus
  // returns to the button). Only one menu is open at a time because the parent holds one id.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: PointerEvent): void => {
      if (wrapRef.current !== null && !wrapRef.current.contains(event.target as Node)) {
        closeRef.current();
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeRef.current();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <span className="rowMenu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        data-row-menu={rowId}
        className="iconButton"
        aria-label={`More for ${name}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="currentColor"
          aria-hidden="true"
        >
          <circle cx="2" cy="7" r="1.4" />
          <circle cx="7" cy="7" r="1.4" />
          <circle cx="12" cy="7" r="1.4" />
        </svg>
      </button>
      {open ? (
        <ul className="rowMenu__list" role="menu">
          {items.map((item) => (
            <li key={item.label} role="none">
              <button
                type="button"
                role="menuitem"
                className="rowMenu__item"
                onClick={() => {
                  onPick();
                  item.run();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}

/** Escape cancels a form from anywhere inside it. */
function cancelOnEscape(onCancel: () => void) {
  return (event: ReactKeyboardEvent<HTMLFormElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };
}

/** The submit and cancel buttons every inline form ends with. */
export function FormButtons({
  submitLabel,
  disabled,
  onCancel,
}: {
  submitLabel: string;
  disabled: boolean;
  onCancel: () => void;
}): JSX.Element {
  return (
    <span className="inlineForm__buttons">
      <button type="button" className="button--secondary-square" onClick={onCancel}>
        Cancel
      </button>
      <button type="submit" className="inlineForm__submit" disabled={disabled}>
        {submitLabel}
      </button>
    </span>
  );
}

/** The server's rule: an '@' with something before it, and a '.' in the domain. */
export function looksLikeEmail(email: string): boolean {
  const at = email.indexOf('@');
  return at >= 1 && email.slice(at + 1).includes('.');
}

export const NOT_AN_EMAIL = "That doesn't look like an email address.";

/** One field with a refusal line under it. Enter submits, Escape cancels. */
export function InlineForm({
  label,
  initial,
  busy,
  error = null,
  submitLabel = 'Save',
  email = false,
  onCancel,
  onSave,
}: {
  label: string;
  initial: string;
  busy: boolean;
  error?: string | null;
  submitLabel?: string;
  email?: boolean;
  onCancel: () => void;
  onSave: (value: string) => Promise<void>;
}): JSX.Element {
  const [value, setValue] = useState(initial);
  const [shape, setShape] = useState<string | null>(null);
  return (
    <div className="inlineForm__box">
      <form
        className="inlineForm"
        noValidate
        onKeyDown={cancelOnEscape(onCancel)}
        onSubmit={(event) => {
          event.preventDefault();
          if (email && value.trim() !== '' && !looksLikeEmail(value.trim())) {
            setShape(NOT_AN_EMAIL);
            return;
          }
          void onSave(value.trim());
        }}
      >
        <input
          className="inlineForm__input"
          type={email ? 'email' : undefined}
          aria-label={label}
          placeholder={label}
          value={value}
          autoFocus
          onChange={(event) => {
            setValue(event.target.value);
            setShape(null);
          }}
        />
        <FormButtons
          submitLabel={submitLabel}
          disabled={busy || value.trim() === ''}
          onCancel={onCancel}
        />
        {(shape ?? error) === null ? null : (
          <p className="inlineForm__error">{shape ?? error}</p>
        )}
      </form>
    </div>
  );
}

/** Puts focus back on a row's "…" button once the row has drawn normally again. */
export function focusRowMenu(rowId: string): void {
  window.requestAnimationFrame(() => {
    document
      .querySelector<HTMLButtonElement>(`[data-row-menu="${CSS.escape(rowId)}"]`)
      ?.focus();
  });
}

/**
 * The in-card question that replaces a row's content when Remove is chosen. It fills the
 * card it sits in (the card is made its positioning box by styles.css), so the card keeps
 * its size and border. Focus starts on Cancel; Escape cancels.
 */
export function RemoveConfirm({
  question,
  actionLabel = 'Remove',
  busy,
  error,
  onCancel,
  onRemove,
}: {
  question: string;
  actionLabel?: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onRemove: () => void;
}): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  return (
    <div
      className="removeConfirm"
      role="group"
      aria-label={question}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      }}
    >
      <div className="removeConfirm__text">
        <p className="removeConfirm__question">{question}</p>
        {error === null ? null : <p className="inlineForm__error">{error}</p>}
      </div>
      <span className="inlineForm__buttons">
        <button
          ref={cancelRef}
          type="button"
          className="button--secondary-square"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="inlineForm__submit"
          disabled={busy}
          onClick={onRemove}
        >
          {actionLabel}
        </button>
      </span>
    </div>
  );
}

export { cancelOnEscape };
